import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import HelpIcon from "@redotech/redo-icons/help-circle.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { EvaluationTime } from "@redotech/redo-return-core-common/return-flow/condition-enums";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import {
  RadioGroupOption,
  RedoRadioGroup,
} from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";

function evaluationTimeOptions(
  t: (key: string) => string,
  orderTimeTooltip: string | undefined,
): RadioGroupOption<EvaluationTime>[] {
  return [
    {
      id: EvaluationTime.TIME_OF_ORDER,
      label: t("Time of order"),
      labelBadge: orderTimeTooltip && (
        <Tooltip title={orderTimeTooltip}>
          <Flex align="center">
            <RedoIcon color="tertiary" Icon={HelpIcon} />
          </Flex>
        </Tooltip>
      ),
    },
    { id: EvaluationTime.TIME_OF_RETURN, label: t("Time of return") },
  ];
}

export function EvaluationTimeRadio({
  value,
  setValue,
  disabled,
  defaultValue,
  label,
  orderTimeTooltip,
}: {
  value: EvaluationTime | undefined;
  setValue: (value: EvaluationTime) => void;
  disabled?: boolean;
  defaultValue: EvaluationTime;
  label: string;
  orderTimeTooltip?: string;
}) {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);

  // The order-time/return-time distinction only exists for Shopify (the live and
  // snapshot data are Shopify-only). Otherwise it always uses the default.
  if (!team.shopifyIntegration) {
    const time =
      defaultValue === EvaluationTime.TIME_OF_ORDER
        ? t("time of order")
        : t("time of return");
    return (
      <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
        {t("{{label}} the {{time}}", { label, time })}
      </Text>
    );
  }

  return (
    <RedoRadioGroup
      disabled={disabled}
      label={label}
      options={evaluationTimeOptions(t, orderTimeTooltip)}
      setValue={setValue}
      size="xs"
      value={value ?? defaultValue}
    />
  );
}
